import React, { useState, useEffect } from "react";
import { StyleSheet, ScrollView, View } from "react-native";
import { colors } from "src/assets";
import { TabControl, NotFound, CallHistoryItem } from "./components";
import { useCallHistory, useCognito, useActiveCall } from "src/hooks";
import { useNavigation } from "@react-navigation/native";
import { tabChangeEmit } from "src/utils/event";
import { IRequest } from "src/domains";
import LocalText from "src/local";
import { MainTabScreenProps } from "src/navigation/types";

enum CallHistoryListTab {
  All = 0,
  Sent = 1,
  Received = 2,
}

export const CallHistoryList: React.FC = () => {
  const navigation =
    useNavigation<MainTabScreenProps<"CallHistoryList">["navigation"]>();
  const [tab, setTab] = useState<CallHistoryListTab>(CallHistoryListTab.All);
  const [cognitoUsername] = useCognito();
  const [calls, callsLoading] = useCallHistory();
  const [callSentItems, setCallSentItems] = useState<Array<IRequest>>([]);
  const [callReceivedItems, setCallReceivedItems] = useState<Array<IRequest>>(
    []
  );
  const [activeCall] = useActiveCall();
  useEffect(() => {
    if (cognitoUsername) {
      setCallSentItems(
        calls.filter((item) => item.senderId === cognitoUsername)
      );
      setCallReceivedItems(
        calls.filter((item) => item.opponentId === cognitoUsername)
      );
    }
  }, [calls, cognitoUsername]);
  useEffect(() => {
    const unsubscribe = navigation.addListener("focus", (e) => {
      tabChangeEmit("CallHistoryList");
    });
    return () => {
      unsubscribe();
    };
  }, [navigation]);
  return (
    <View style={styles.container}>
      <TabControl
        tabs={[
          { label: LocalText.screens.callHistoryList.tabAll },
          { label: LocalText.screens.callHistoryList.tabSent },
          { label: LocalText.screens.callHistoryList.tabReceived },
        ]}
        selectedIndex={tab}
        onChange={(index) => setTab(index)}
      />
      {tab === CallHistoryListTab.All ? (
        <>
          {!callsLoading && calls.length === 0 ? (
            <NotFound text={LocalText.screens.callHistoryList.notFound} />
          ) : null}
          {calls.length > 0 && cognitoUsername ? (
            <ScrollView showsVerticalScrollIndicator={false}>
              {calls.map((item, index) => (
                <CallHistoryItem
                  key={index}
                  color={index % 2 === 0 ? "white" : "transparent"}
                  cognitoUserId={cognitoUsername}
                  item={item}
                  active={activeCall?.id === item.id}
                  onPress={() => {
                    if (activeCall?.id === item.id) {
                      navigation.push("Call");
                    } else {
                      navigation.push("CallHistory", { callId: item.id });
                    }
                  }}
                />
              ))}
            </ScrollView>
          ) : null}
        </>
      ) : null}
      {tab === CallHistoryListTab.Sent ? (
        <>
          {!callsLoading && callSentItems.length === 0 ? (
            <NotFound text={LocalText.screens.callHistoryList.notFound} />
          ) : null}
          {callSentItems.length > 0 && cognitoUsername ? (
            <ScrollView showsVerticalScrollIndicator={false}>
              {callSentItems.map((item, index) => (
                <CallHistoryItem
                  key={index}
                  color={index % 2 === 0 ? "white" : "transparent"}
                  cognitoUserId={cognitoUsername}
                  item={item}
                  active={activeCall?.id === item.id}
                  onPress={() => {
                    if (activeCall?.id === item.id) {
                      navigation.push("Call");
                    } else {
                      navigation.push("CallHistory", { callId: item.id });
                    }
                  }}
                />
              ))}
            </ScrollView>
          ) : null}
        </>
      ) : null}
      {tab === CallHistoryListTab.Received ? (
        <>
          {!callsLoading && callReceivedItems.length === 0 ? (
            <NotFound text={LocalText.screens.callHistoryList.notFound} />
          ) : null}
          {callReceivedItems.length > 0 && cognitoUsername ? (
            <ScrollView showsVerticalScrollIndicator={false}>
              {callReceivedItems.map((item, index) => (
                <CallHistoryItem
                  key={index}
                  color={index % 2 === 0 ? "white" : "gray"}
                  cognitoUserId={cognitoUsername}
                  item={item}
                  active={activeCall?.id === item.id}
                  onPress={() => {
                    if (activeCall?.id === item.id) {
                      navigation.push("Call");
                    } else {
                      navigation.push("CallHistory", { callId: item.id });
                    }
                  }}
                />
              ))}
            </ScrollView>
          ) : null}
        </>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.white,
  },
});
